import React, { useEffect, useState } from 'react';
import {
	Button,
	Card,
	Divider,
	Modal,
	notification,
	Spin,
	Tag,
	Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import BuildingTag from '../../../../components/BuildingTag/index.tsx';
import { useBuildingContext } from '../../../../context/BuildingContext';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
	useGetGroupedMaterialReservationsQuery,
	useLazyGetMaterialReservationsQuery,
	useUpdateMaterialReservationMutation,
	useUpdateMultipleMaterialReservationCutStatusesMutation,
	usePrintCutTagMutation,
} from '../../../../redux/api/material';
import {
	useGetUsersQuery,
	UserQueryInterface,
} from '../../../../redux/api/users.ts';
import { MaterialReservation } from '../../../../interfaces/MaterialInventory.interface';
import { GroupedMaterialReservation } from '../../../../components/Operations/SawCutQueue/types';
import QueueClassTags from '../../../../components/Operations/SawCutQueue/QueueClassTags';
import QueueBarList from '../../../../components/Operations/SawCutQueue/QueueBarList';
import ExpandableContent from '../../../../components/Operations/SawCutQueue/SawCutQueueTable/ExpandableContent/index.tsx';
import CutActions from '../../../../components/Operations/SawCutQueue/SawCutQueueTable/CutActions.tsx';
import CutMaterialModal from '../../../../components/Operations/SawCutQueue/SawCutQueueTable/CutMaterialModal/index.tsx';
import MaterialLocationsSummaryModal from '../../../../components/Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal/index.tsx';
import {
	ApiErrorAlert,
	getApiErrorMessage,
} from '../../../../components/ApiErrorAlert';
import { IoIosPrint } from 'react-icons/io';
import {
	getDueDateColor,
	dueDateBorderColor,
} from '../../../../utils/dueDateColor';
import { getDisplaySize } from '../../../../utils';
import {
	TABLET_TITLE_STYLE,
	TABLET_BACK_BUTTON_STYLE,
} from '../../../../components/Operations/tabletStyles';
import { usePrintLocation } from '../../../../context/PrintLocationContext';

const { Text, Title } = Typography;

interface IMaterialReservations {
	count: number;
	results: MaterialReservation[];
}

const SawCutQueueDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const { buildingId } = useBuildingContext();

	// Record passed via navigation state from SawCutCard (undefined on hard refresh)
	const stateRecord = location.state?.record as
		| GroupedMaterialReservation
		| undefined;
	const returnTo =
		(location.state?.returnTo as string | undefined) ??
		'/tablet/operations/cutting';

	// Always fetch group data so the header status stays in sync after updates.
	//
	// Scoped to the same building as the queue that linked here. Classification
	// reads the bars the filter leaves behind, so an unscoped fetch can answer
	// "ready to cut" on a bar in another building for a card that said "no
	// usable bar" — on the one screen where the operator picks the bar.
	const { data: fetchedGroupData, refetch: refetchGroupData } =
		useGetGroupedMaterialReservationsQuery(
			{ group_key: id, page_size: 1, ...(buildingId && { building: buildingId }) },
			{ skip: !id }
		);
	const record: GroupedMaterialReservation | undefined =
		(fetchedGroupData?.results?.[0] as
			| GroupedMaterialReservation
			| undefined) ?? stateRecord;

	// Users for operator selection in CutMaterialModal
	const userQuery: UserQueryInterface = { groups__name: 'Machine Operators' };
	const { data: userDataFromQuery } = useGetUsersQuery(userQuery);
	const userData = userDataFromQuery?.results ?? [];

	// Individual reservations for the sub-table
	const [materialReservations, setMaterialReservations] =
		useState<IMaterialReservations>({ count: 0, results: [] });
	const [materialRervPageNumber, setMaterialRervPageNumber] = useState(1);
	const [loadingMaterialReservations, setLoadingMaterialReservations] =
		useState(false);

	// Cut modal state
	const [isCutMaterialVisible, setisCutMaterialVisible] = useState(false);
	const [selectMaterialToCut, setSelectMaterialToCut] = useState<
		MaterialReservation[]
	>([]);
	const [radioButtonLoading, setRadioButtonLoading] = useState(false);

	// Remnant summary modal state
	const [isRemnantSummaryVisible, setIsRemnantSummaryVisible] = useState(false);
	const [remnantSummaryData, setRemnantSummaryData] = useState<{
		materialInfo: any;
		lengths: any[];
	}>({ materialInfo: {}, lengths: [] });

	const [loadingPrint, setLoadingPrint] = useState(false);
	const [groupCutStatusError, setGroupCutStatusError] = useState<string | null>(
		null
	);

	// Navigation guard state (Ticket 6)
	const [guardVisible, setGuardVisible] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<string | null>(
		null
	);
	// Ref so the popstate handler always sees the latest value without re-registering
	const needsGuardRef = React.useRef(true);

	const [lazyFetchReservations] = useLazyGetMaterialReservationsQuery();
	const [updateMaterialReservation] = useUpdateMaterialReservationMutation();
	const [updateMultipleReservationStatuses] =
		useUpdateMultipleMaterialReservationCutStatusesMutation();
	const [printCutTagMutation] = usePrintCutTagMutation();
	const { promptForBuilding } = usePrintLocation();

	// Block browser back/forward button (Ticket 6)
	// Push an extra history entry on mount so browser back stays on this page.
	useEffect(() => {
		window.history.pushState(null, '', window.location.href);

		const handlePopState = () => {
			if (!needsGuardRef.current) {
				navigate(returnTo);
				return;
			}
			// Re-push so repeated back presses don't escape without confirming.
			window.history.pushState(null, '', window.location.href);
			setPendingNavigation(returnTo);
			setGuardVisible(true);
		};

		window.addEventListener('popstate', handlePopState);
		return () => window.removeEventListener('popstate', handlePopState);
	}, [navigate, returnTo]);

	// Warn on tab close / hard refresh — only when there are pending items
	useEffect(() => {
		const handler = (e: BeforeUnloadEvent) => {
			if (needsGuardRef.current) e.preventDefault();
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, []);

	// Fetch individual reservations on mount
	useEffect(() => {
		if (id) {
			fetchReservations(id, 1);
		}
	}, [id]);

	async function fetchReservations(
		groupKey: string,
		page: number,
		pageSize = 20
	) {
		setLoadingMaterialReservations(true);
		return lazyFetchReservations({
			group_key: groupKey,
			page,
			page_size: pageSize,
		})
			.unwrap()
			.then((res) => {
				const results = (res?.results || []).map(
					(item: MaterialReservation) => ({
						...item,
						group_key: groupKey,
					})
				);
				setMaterialReservations({ count: res?.count ?? 0, results });
				return results;
			})
			.finally(() => setLoadingMaterialReservations(false));
	}

	const handleMaterialReservationPaginationChange = async (newPage: number) => {
		if (!id) return;
		setMaterialRervPageNumber(newPage);
		await fetchReservations(id, newPage);
	};

	async function handleUpdateMaterialReservation(
		reservationId: string,
		status: string,
		cut_by?: string | number | null,
		orderLineItemId?: string
	) {
		setRadioButtonLoading(true);
		await updateMaterialReservation({
			id: reservationId,
			values: { material_cut_status: status, cut_by },
			// Scope the cache invalidation to this line item instead of every
			// cached one.
			orderLineItemId,
		})
			.unwrap()
			.then(async () => {
				if (id) {
					await fetchReservations(id, materialRervPageNumber);
					await refetchGroupData();
				}
			})
			.finally(() => setRadioButtonLoading(false));
	}

	// Group-level cut action (from the CutActions radio at the top of the detail page)
	const handleGroupCutAction = async (value: string) => {
		if (!id || !record) return;
		if (value === 'Cut') {
			setSelectMaterialToCut([]);
			const allReservations = await fetchReservations(
				id,
				1,
				record.reservations_count
			);
			const pendingMaterials =
				allReservations?.filter(
					(item: MaterialReservation) => item.material_cut_status !== 'Cut'
				) || [];
			setSelectMaterialToCut(pendingMaterials);
			setisCutMaterialVisible(true);
		} else {
			setGroupCutStatusError(null);
			setRadioButtonLoading(true);
			try {
				await updateMultipleReservationStatuses({
					group_key: id,
					material_cut_status: value,
				}).unwrap();
				if (id) {
					await fetchReservations(id, materialRervPageNumber);
					await refetchGroupData();
				}
			} catch (err) {
				setGroupCutStatusError(getApiErrorMessage(err));
			} finally {
				setRadioButtonLoading(false);
			}
		}
	};

	const handleShowRemnantSummary = async () => {
		if (!id || !record) return;
		const allLengths = await fetchReservations(
			id,
			1,
			record.reservations_count
		);
		const materialInfo = {
			type: record.material_type,
			width: record.material_width ? Number(record.material_width) : undefined,
			height: record.material_height
				? Number(record.material_height)
				: undefined,
			outer_diameter: record.material_outer_diameter
				? Number(record.material_outer_diameter)
				: undefined,
			inner_diameter: record.material_inner_diameter
				? Number(record.material_inner_diameter)
				: undefined,
			wall_thickness: record.material_wall_thickness
				? Number(record.material_wall_thickness)
				: undefined,
			shape: record.material_shape,
		};
		const lengthsData = allLengths
			.filter(
				(res: MaterialReservation) =>
					res.material_cut_status === 'Cut' ||
					res.material_cut_status === 'In Progress'
			)
			.map((res: MaterialReservation) => ({
				id: res.material_length?.id || res.id,
				friendlyId: res.material_length?.friendly_id || 'N/A',
				location:
					res.material_length?.inventory_location?.display_name ?? 'Unknown',
				length: Number(res.material_length?.current_length || 0),
				isScrapped: res.material_length?.status === 'Scrapped',
			}));
		setRemnantSummaryData({ materialInfo, lengths: lengthsData });
		setIsRemnantSummaryVisible(true);
	};

	const handlePrint = async () => {
		if (!id) return;
		promptForBuilding(
			async (selectedBuildingId) => {
				setLoadingPrint(true);
				try {
					await printCutTagMutation({
						group_key: id,
						...(selectedBuildingId ? { building_id: selectedBuildingId } : {}),
					}).unwrap();
					notification.success({
						duration: 10,
						message: 'Print Successful',
						description: 'Your print was successfully sent to the printer.',
						placement: 'topRight',
					});
				} catch {
					notification.error({
						duration: 10,
						message: 'Error',
						description: 'Error encountered while attempting to Print.',
						placement: 'topRight',
					});
				} finally {
					setLoadingPrint(false);
				}
			},
			{ description: 'Select the building to print the cut tag at.' },
		);
	};

	const handleConfirmAndLeave = () => {
		const dest = pendingNavigation ?? returnTo;
		setGuardVisible(false);
		setPendingNavigation(null);
		navigate(dest);
	};

	const handleCancelGuard = () => {
		setGuardVisible(false);
		setPendingNavigation(null);
	};

	// Derive group status from record or from fetched reservations
	const groupStatus = record?.group_status;
	// Keep ref in sync so event listeners always see the latest value
	needsGuardRef.current = groupStatus !== 'Cut';
	const showPrintAndSummary =
		record?.group_cut_statuses_summary?.includes('Cut') ||
		record?.group_cut_statuses_summary?.includes('In Progress');

	const color = getDueDateColor(
		record?.order_line_item_required_partner_ship_date
	);

	if (!id) return null;

	return (
		<div className="flex flex-col w-full p-3 gap-4 saw-cut-detail-page">
			<ApiErrorAlert
				message={groupCutStatusError}
				onDismiss={() => setGroupCutStatusError(null)}
			/>
			{/* Header */}
			<div className="flex items-center justify-between">
				<Title level={2} style={{ margin: 0, ...TABLET_TITLE_STYLE }}>
					Saw Cut — Detail
				</Title>
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() => {
						setPendingNavigation(returnTo);
						setGuardVisible(true);
					}}
					type="primary"
					size="large"
					style={TABLET_BACK_BUTTON_STYLE}
				>
					Back
				</Button>
			</div>

			<Divider style={{ margin: '0' }} />

			{/* Summary card */}
			{record ? (
				<Card
					style={{ borderLeft: `4px solid ${dueDateBorderColor[color]}` }}
					styles={{ body: { padding: 24 } }}
				>
					<div className="flex flex-col gap-3">
						<div className="flex items-start justify-between gap-3">
							<div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2 }}>
								{record.order_name}
							</div>
							<Link
								to={`/orders/line-items/${record.order_line_item_id}/`}
								target="_blank"
								rel="noopener noreferrer"
								style={{ fontSize: 16, whiteSpace: 'nowrap' }}
							>
								View Order Line
							</Link>
						</div>

						<QueueClassTags record={record} size="detail" />

						<div className="flex gap-4">
							<div
								style={{
									width: 220,
									height: 220,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									border: '1px solid #f0f0f0',
									borderRadius: 8,
									flexShrink: 0,
								}}
							>
								{record.thumbnail_file ? (
									<img
										src={record.thumbnail_file}
										alt="part thumbnail"
										style={{ width: 220, height: 220, objectFit: 'contain' }}
									/>
								) : (
									<span style={{ color: '#999', fontSize: 16 }}>No image</span>
								)}
							</div>
							<div
								style={{ flex: 1, minWidth: 0 }}
								className="flex flex-col gap-3"
							>
								<div style={{ fontSize: 16, color: '#888' }}>
									{record.order_line_item_name}
								</div>
								<div style={{ fontSize: 16 }}>
									<span style={{ color: '#888' }}>Reserving for:</span>{' '}
									<strong style={{ color: '#222' }}>
										{record.reserved_for_description?.trim() || '—'}
									</strong>
								</div>
								<div style={{ fontSize: 16 }}>
									{record.material_type} · {record.material_shape} · {getDisplaySize(record)}
								</div>
								<div
									className="flex flex-wrap gap-x-6 gap-y-2"
									style={{ fontSize: 16 }}
								>
									<span>
										<span style={{ color: '#888' }}>Cut Len:</span>{' '}
										<strong>{record.material_cut_length}</strong>
									</span>
									<span>
										<span style={{ color: '#888' }}>Qty:</span>{' '}
										<strong>{record.total_material_cut_quantity}</strong>
									</span>
									<span>
										<span style={{ color: '#888' }}>Actual:</span>{' '}
										<strong>{record.actual_cut_quantity}</strong>
									</span>
									<span>
										<span style={{ color: '#888' }}>Reserved:</span>{' '}
										<strong>{record.total_reserved_length}</strong>
									</span>
									<span>
										<span style={{ color: '#888' }}>Bars:</span>{' '}
										<strong>{record.reservations_count}</strong>
									</span>
								</div>
								<div
									className="flex items-center gap-3"
									style={{ fontSize: 16 }}
								>
									<span style={{ color: '#888' }}>Due:</span>
									<strong>
										{record.order_line_item_required_partner_ship_date
											? dayjs(
													record.order_line_item_required_partner_ship_date
											  ).format('MM/DD/YYYY')
											: '—'}
									</strong>
									{groupStatus && (
										<Tag
											style={{ fontSize: 16 }}
											color={
												groupStatus === 'Cut'
													? 'green'
													: groupStatus === 'In Progress'
													? 'blue'
													: 'default'
											}
										>
											{groupStatus}
										</Tag>
									)}
									<BuildingTag
										name={record.building_name}
										style={{ fontSize: 16 }}
									/>
								</div>
								{/* The bar list below replaces this line when the queue
								    classification is available, since it shows the same IDs
								    with the status of each one. */}
								{record.bars?.length ? null : (
									<div style={{ fontSize: 16, color: '#888' }}>
										IDs: {record.material_length_friendly_ids?.join(', ')}
									</div>
								)}
							</div>
						</div>
					</div>
				</Card>
			) : (
				<Card>
					<Text type="secondary">Loading group details…</Text>
				</Card>
			)}

			{record?.bars?.length ? (
				<Card
					title={<span style={{ fontSize: 16 }}>Bars in this group</span>}
					styles={{ body: { padding: 20 } }}
				>
					<QueueBarList bars={record.bars} />
				</Card>
			) : null}

			{/* Group-level cut actions */}
			{groupStatus && (
				<Card
					title={<span style={{ fontSize: 16 }}>Cut Status</span>}
					styles={{ body: { padding: 20 } }}
				>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr',
							alignItems: 'center',
							gap: 16,
						}}
					>
						<div className="flex justify-center">
							<Spin spinning={radioButtonLoading}>
								<CutActions
									handleClick={handleGroupCutAction}
									cut_status={groupStatus}
									disableNotCut={(record?.actual_cut_quantity ?? 0) > 0}
									styles={{ flexDirection: 'row' }}
								/>
							</Spin>
						</div>
						{showPrintAndSummary && (
							<div className="flex gap-2 justify-center">
								<Button
									type="primary"
									icon={<IoIosPrint />}
									onClick={handlePrint}
									size="large"
									style={{ fontSize: 16, height: 'auto', padding: '8px 20px' }}
									loading={loadingPrint}
								>
									Print
								</Button>
								<Button
									type="primary"
									onClick={handleShowRemnantSummary}
									size="large"
									style={{ fontSize: 16, height: 'auto', padding: '8px 20px' }}
								>
									Remnant Summary
								</Button>
							</div>
						)}
					</div>
				</Card>
			)}

			{/* Material reservations sub-table */}
			<Card
				title={<span style={{ fontSize: 16 }}>Material Reservations</span>}
				styles={{ body: { padding: 16 } }}
			>
				<ExpandableContent
					materialReservations={materialReservations}
					loadingMaterialReservations={loadingMaterialReservations}
					userData={userData}
					setMaterialReservations={setMaterialReservations}
					handleClickCutItem={async (action, material_reservation) => {
						if (action === 'Cut' && material_reservation) {
							setSelectMaterialToCut([material_reservation]);
							setisCutMaterialVisible(true);
						} else {
							await handleUpdateMaterialReservation(
								material_reservation.id,
								action,
								material_reservation.cut_by,
								material_reservation.order_line_item?.id
							);
							if (id) {
								fetchReservations(id, materialRervPageNumber);
							}
						}
					}}
					pagination={{
						current: materialRervPageNumber,
						pageSize: 20,
						total: materialReservations.count,
						onChange: handleMaterialReservationPaginationChange,
					}}
				/>
			</Card>

			{/* Modals */}
			{isCutMaterialVisible && (
				<CutMaterialModal
					visible={isCutMaterialVisible}
					onCancel={() => setisCutMaterialVisible(false)}
					materialReservations={selectMaterialToCut}
					overrideMaterialReservations={setSelectMaterialToCut}
					loadingMaterialReservations={loadingMaterialReservations}
					userData={userData}
					setRadioButtonLoading={setRadioButtonLoading}
					refetch={async () => {
						if (id) {
							await fetchReservations(id, materialRervPageNumber);
						}
						// Also refresh the group-level data so the "Cut Status"
						// card/tag reflects the new status after cutting (the
						// modal otherwise only refreshes the reservations table).
						await refetchGroupData();
					}}
					handleFetchReservationsById={(groupKey) =>
						fetchReservations(groupKey, materialRervPageNumber)
					}
				/>
			)}

			{isRemnantSummaryVisible && (
				<MaterialLocationsSummaryModal
					visible={isRemnantSummaryVisible}
					onComplete={() => setIsRemnantSummaryVisible(false)}
					materialInfo={remnantSummaryData.materialInfo}
					lengths={remnantSummaryData.lengths}
					title="Remnant Summary"
					source="cutting"
				/>
			)}

			{/* Navigation guard modal (Ticket 6) */}
			<Modal
				open={guardVisible}
				title={<span style={{ fontSize: 16 }}>Before you leave...</span>}
				centered
				closable={false}
				maskClosable={false}
				footer={
					<div className="flex justify-end gap-2">
						<Button
							onClick={handleCancelGuard}
							style={{ fontSize: 16, height: 'auto', padding: '8px 24px' }}
						>
							No
						</Button>
						<Button
							type="primary"
							onClick={handleConfirmAndLeave}
							style={{ fontSize: 16, height: 'auto', padding: '8px 24px' }}
						>
							Yes
						</Button>
					</div>
				}
			>
				<div style={{ fontSize: 16 }}>Did you update the cut statuses?</div>
			</Modal>
		</div>
	);
};

export default SawCutQueueDetail;
